import { Helmet } from "react-helmet-async";

/**
 * SEOHead — one component to synchronously set title / description /
 * canonical / Open Graph / Twitter / JSON-LD for a page.
 *
 * Uses react-helmet-async so all mutations happen inside the React
 * render tree (not `useEffect`). This is critical because:
 *   1. Non-Googlebot crawlers (Bingbot, YandexBot, most AI answer
 *      engines) do NOT execute post-render `useEffect` mutations —
 *      they read <head> at HTML parse time.
 *   2. React 18 renders Helmet children before the client-side
 *      hydration effect fires, so the tags are attached earlier in
 *      the pipeline.
 *
 * Every SEO / marketing / article page must use this component in
 * place of manual `document.title` + `document.head.appendChild`.
 */
export default function SEOHead({
  title,
  description,
  canonical,
  keywords,
  image = "https://facetra.studio/og-image.jpg",
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title?.endsWith("Facetra") ? title : `${title} — Facetra`;
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description ? <meta name="description" content={description} /> : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      {canonical ? <link rel="canonical" href={canonical} /> : null}
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Facetra" />
      <meta property="og:locale" content="en_IN" />
      {canonical ? <meta property="og:url" content={canonical} /> : null}
      <meta property="og:title" content={fullTitle} />
      {description ? (
        <meta property="og:description" content={description} />
      ) : null}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description ? (
        <meta name="twitter:description" content={description} />
      ) : null}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
